import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertsConfig, ChangesAlerts } from '../../../types'
import { ListType, SettingsType } from '../../constants/enums'
import {
    AlertType,
    UEOPT_APP_ALERT_SETTINGS_TYPES,
} from '../../mfcLogic/ueopts'
import { ALERTSSTATE } from '../../state'
import { pubSubAlerts } from '../../state/pubSub'
import {
    clearAlerts as clearAlertState,
    resetUnread as resetUnreadState,
} from '../../state/state.alerts'
import { useList } from './useList'
import { useSettingSelector } from './useSettings'

const unreadInitialState = (skipUnread: boolean) => {
    if (skipUnread) return 0
    return ALERTSSTATE.unread
}

const alertsInitialState = (id: number, updatesOnly?: boolean) => {
    const alerts = Array.from(ALERTSSTATE.alerts.values())
    if (updatesOnly) return []
    if (!id) return alerts
    return alerts.filter(({ id: target }) => id === target)
}

interface AlertsData {
    alerts: ChangesAlerts[]
    unread: number
}

interface UseAlerts {
    config?: AlertsConfig
    updatesOnly?: boolean
    unreadCount?: boolean
    unreadID?: number
    id?: number
}

// the "Notify Model Activity (Header)" setting; token alerts always pass
export const useHeaderAlertsFilter = () => {
    const [alertsSettings] = useSettingSelector(SettingsType.DEFAULT, 'alerts')
    const setting = Number(alertsSettings)
    return useCallback(
        (alert: ChangesAlerts) => {
            if (alert.key === 'tokens') return true
            if (setting === UEOPT_APP_ALERT_SETTINGS_TYPES[AlertType.OFF].bit)
                return false
            if (
                alert.news &&
                setting === UEOPT_APP_ALERT_SETTINGS_TYPES[AlertType.STATUS].bit
            )
                return false
            if (
                !alert.news &&
                setting === UEOPT_APP_ALERT_SETTINGS_TYPES[AlertType.NEWS].bit
            )
                return false
            return true
        },
        [setting],
    )
}

export const DEFAULT_CONFIG = {
    isPublicShow: [true],
    isTruePrivate: [true],
    isOnlyPrivate: [true],
    isGroupShow: [true],
    isClubShow: [true],
    isOffline: [true],
    isOnline: [true],
    isAway: [true],
}

export function useAlerts({
    updatesOnly,
    unreadCount,
    config = DEFAULT_CONFIG,
    id,
}: UseAlerts) {
    const skipUnread = !unreadCount || updatesOnly || !!id
    const friendsList = useList(ListType.FRIENDS, { returnIDs: true })
    const bookmarksList = useList(ListType.BOOKMARKS, { returnIDs: true })
    const friendsAndBookmarks = useMemo(
        () => new Set([...friendsList, ...bookmarksList]),
        [friendsList, bookmarksList],
    )
    const [alerts, setAlerts] = useState(() =>
        alertsInitialState(id, updatesOnly),
    )
    const [unread, setUnread] = useState(() => unreadInitialState(skipUnread))

    const resetUnread = useCallback(() => {
        if (skipUnread) return
        setUnread(0)
        resetUnreadState()
    }, [skipUnread])

    const clearAlerts = useCallback(() => {
        setUnread(0)
        clearAlertState()
    }, [])

    useEffect(() => {
        const callback = (data: AlertsData) => {
            const { alerts: incomingAlerts = [], unread } = data
            // filter out status alerts from users
            // that we do not have as friends/bookmarks
            // but we subscribe to for news alerts only
            const filteredAlerts = incomingAlerts.filter((alert) => {
                if (alert.key === 'tokens') return true
                if (!alert.news && !friendsAndBookmarks.has(alert.id))
                    return false
                else return true
            })
            if (unreadCount && !updatesOnly && !id) {
                setUnread(unread)
            }
            if (updatesOnly) {
                setAlerts([...filteredAlerts])
                return
            }
            if (id) {
                setAlerts((prevAlerts) => [...prevAlerts, ...filteredAlerts])
                return
            }
            setAlerts(Array.from(ALERTSSTATE.alerts.values()))
        }

        if (id) {
            return pubSubAlerts.on(`ALERT::${id}`, callback, config)
        }

        return pubSubAlerts.on('ALERT', callback, config)
    }, [friendsAndBookmarks, config, unreadCount, updatesOnly, id])

    if (skipUnread) {
        return { alerts }
    }

    return { alerts, unread, clearAlerts, resetUnread }
}
